const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export type ReportColumn =
  | 'activity_status'
  | 'customers_created'
  | 'customers_worked_in'
  | 'reports_total'
  | 'reports_by_workflow'
  | 'documents_uploaded'
  | 'login_count'
  | 'last_activity';

export interface UsageReportRow {
  user: { id: string; name: string; email: string; status: string };
  customersCreated?: number;
  customersWorkedIn?: number;
  reportsTotal?: number;
  reportsByWorkflow?: Record<string, number>;
  documentsUploaded?: number;
  loginCount?: number;
  lastActivity?: string | null;
}

export interface UsageReportResponse {
  company: { id: string; name: string };
  dateFrom: string;
  dateTo: string;
  generatedAt: string;
  columns: ReportColumn[];
  rows: UsageReportRow[];
  licenseUtilization?: { used: number; total: number };
}

export interface ScheduledReport {
  id: string;
  companyId: string;
  createdById: string;
  name: string;
  columns: ReportColumn[];
  dateRangeMode: 'last_month' | 'last_30d';
  recipients: string[];
  nextRunAt: string;
  lastRunAt: string | null;
  enabled: boolean;
}

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('riskninja-token')}`,
  };
}

export interface UsageRequestBody {
  columns: ReportColumn[];
  dateRange: { mode?: 'last_month' | 'last_30d'; from?: string; to?: string };
  userIds?: string[];
}

export const adminReportService = {
  async preview(body: UsageRequestBody): Promise<UsageReportResponse> {
    const r = await fetch(`${API_BASE_URL}/api/admin/reports/usage`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error((await r.json()).error || 'Failed to generate report');
    return r.json();
  },

  async downloadPdf(body: UsageRequestBody, filename: string): Promise<void> {
    const r = await fetch(`${API_BASE_URL}/api/admin/reports/usage/pdf`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error('Failed to generate PDF');
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  async listScheduled(): Promise<ScheduledReport[]> {
    const r = await fetch(`${API_BASE_URL}/api/admin/reports/scheduled`, { headers: authHeaders() });
    if (!r.ok) throw new Error('Failed to load scheduled reports');
    const data = await r.json();
    return data.items || [];
  },

  async createScheduled(input: {
    name: string;
    columns: ReportColumn[];
    dateRangeMode: 'last_month' | 'last_30d';
    recipients: string[];
    enabled?: boolean;
  }): Promise<ScheduledReport> {
    const r = await fetch(`${API_BASE_URL}/api/admin/reports/scheduled`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    if (!r.ok) throw new Error((await r.json()).error || 'Failed to create');
    return r.json();
  },

  async updateScheduled(id: string, patch: Partial<ScheduledReport>): Promise<ScheduledReport> {
    const r = await fetch(`${API_BASE_URL}/api/admin/reports/scheduled/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(patch),
    });
    if (!r.ok) throw new Error('Failed to update');
    return r.json();
  },

  async deleteScheduled(id: string): Promise<void> {
    const r = await fetch(`${API_BASE_URL}/api/admin/reports/scheduled/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!r.ok) throw new Error('Failed to delete');
  },

  async runScheduledNow(id: string): Promise<{ ok: boolean; sent: number; total: number }> {
    const r = await fetch(`${API_BASE_URL}/api/admin/reports/scheduled/${id}/run`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!r.ok) throw new Error('Failed to run');
    return r.json();
  },
};
